/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { Text, View, LargeCloseIcon, Pressable, Icon } from '@components/atoms'

import { grey } from '@mui/material/colors'
import { useAppSelector } from '@redux/index'
import { useNavigate } from 'react-router-dom'

interface PropsType {
	title: string
	customTitle?: JSX.Element
	className?: string
	onCloseClick?: () => void
	size?: 'small' | 'middle' | 'large'
	titleStyle?: React.CSSProperties
	children?: React.ReactNode
	showCloseButton?: boolean
	leftSideContent?: JSX.Element
}

export const FullModalLayout = (props: PropsType) => {
	// hook
	const navigate = useNavigate()

	// redux state
	const backgroundColor: string = useAppSelector(
		state => state.config.mainBackgroundColor,
	)

	// variables
	const size: 'small' | 'middle' | 'large' = props.size ?? 'middle'
	const outerPaddingValue: string =
		size === 'small'
			? '20rem'
			: size === 'middle'
			? '17rem'
			: size === 'large'
			? '17rem'
			: '17rem'
	const innerPaddingValue: string =
		size === 'small'
			? '14rem'
			: size === 'middle'
			? '12rem'
			: size === 'large'
			? '4rem'
			: '12rem'
	const showCloseButton: boolean = props.showCloseButton ?? true

	const onBackClick = React.useCallback(() => {
		if (props.onCloseClick !== undefined) {
			props.onCloseClick()
		}

		navigate(-1)
	}, [props.onCloseClick])

	return (
		<View css={style}>
			<View className="fullModalView">
				{showCloseButton && (
					<View>
						<Pressable className="closeButton" onClick={onBackClick}>
							<LargeCloseIcon strokeColor={backgroundColor} />
						</Pressable>
						<Pressable className="backArrowButton" onClick={onBackClick}>
							<Icon name="chevron-left" className="backArrowIcon" />
						</Pressable>
					</View>
				)}

				<View
					className="contentView"
					css={{ paddingLeft: outerPaddingValue, paddingRight: outerPaddingValue }}
				>
					<View className="leftMarginView" css={{ width: outerPaddingValue }}>
						{props.leftSideContent}
					</View>

					<View
						className={`contentInnerView ${props.className ?? ''}`}
						css={{ paddingLeft: innerPaddingValue, paddingRight: innerPaddingValue }}
					>
						<View className="titleView">
							{props.customTitle ? (
								props.customTitle
							) : (
								<Text
									className="titleText"
									category="h1"
									css={[{ margin: 0 }, { ...props.titleStyle }]}
								>
									{props.title}
								</Text>
							)}
						</View>

						{props.children}
					</View>
				</View>
			</View>
		</View>
	)
}

const style = css({
	'.fullModalView': {
		position: 'fixed',
		width: '100vw',
		height: '100vh',
		overflow: 'hidden',

		'.closeButton': {
			display: 'block',
			position: 'absolute',
			zIndex: 10,
			top: '1rem',
			right: '1rem',
		},

		'.backArrowButton': {
			display: 'none',
		},

		'.contentView': {
			display: 'flex',
			justifyContent: 'center',
			height: '100%',

			'.leftMarginView': {
				position: 'absolute',
				height: '100vh',
				top: 0,
				left: 0,
				background: grey[200],
			},

			'.contentInnerView': {
				position: 'relative',
				height: 'fit-contents',
				width: '60rem',
				paddingTop: '1rem',
				overflow: 'auto',

				'.titleView': {
					display: 'flex',
					justifyContent: 'flex-start',
					alignItems: 'center',
					height: 'fit-contents',
					padding: '3rem 0',

					'.titleText': {},
				},
			},
		},
	},

	'@media (max-width: 1280px)': {
		'.fullModalView': {
			height: 'calc(100% - 1rem)',

			'.contentView': {
				paddingLeft: '3rem',
				paddingRight: '3rem',

				'.leftMarginView': {
					display: 'none',
				},
			},
		},
	},

	'@media (max-width: 900px)': {
		'.fullModalView': {
			'.closeButton': {
				display: 'none',
			},

			'.backArrowButton': {
				display: 'block',
				position: 'absolute',
				zIndex: 10,
				top: '1rem',
				left: '0.5rem',
			},

			'.contentView': {
				paddingLeft: '1rem',
				paddingRight: '1rem',

				'.leftMarginView': {
					display: 'none',
				},
			},
		},
	},

	'@media (max-width: 700px)': {
		'.fullModalView': {
			'.contentView': {
				paddingLeft: '0',
				paddingRight: '0',

				'.contentInnerView': {
					paddingLeft: '1rem',
					paddingRight: '1rem',

					'.titleView': {
						paddingTop: '3rem',
						paddingBottom: '1rem',
					},
				},
			},
		},
	},

	'@media (max-width: 450px)': {
		'.fullModalView': {
			'.contentView': {
				'.contentInnerView': {
					'.titleView': {
						'.titleText': {
							fontSize: '27px',
						},
					},
				},
			},
		},
	},

	'@media (max-width: 400px)': {
		'.fullModalView': {
			'.contentView': {
				'.contentInnerView': {
					'.titleView': {
						paddingBottom: '0.5rem',
						'.titleText': {
							fontSize: '21px',
						},
					},
				},
			},
		},
	},

	'@media (max-width: 300px)': {
		'.fullModalView': {
			'.contentView': {
				'.contentInnerView': {
					'.titleView': {
						'.titleText': {
							fontSize: '20px',
						},
					},
				},
			},
		},
	},
})
